import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { apiClient } from '../config/api';
import { showSuccess, showError } from '../utils/toast';
import './Users.css';

const Avatars = () => {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const menuRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, avatar: null, typedName: '', error: '' });
  // Avatar ids whose Family Group is due for deletion (60+ days past the last paid
  // subscription, no active subscription). The backend enforces this rule; here it
  // only decides whether the Delete action is offered. null = not loaded yet.
  const [eligibleIds, setEligibleIds] = useState(null);

  useEffect(() => { fetchAvatars(); fetchEligibility(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAvatars = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await apiClient.get('/admin/avatars', token);
      const list = response?.data ?? (Array.isArray(response) ? response : []);
      setAvatars(Array.isArray(list) ? list : []);
    } catch (err) {
      showError('Failed to load avatars: ' + (err.message || 'Unknown error'));
      setAvatars([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibility = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiClient.get('/admin/deletion-review', token);
      const groups = response?.data?.groups ?? [];
      const ids = new Set();
      groups.forEach(g => (g.avatars || []).forEach(a => ids.add(a.avatar_id)));
      setEligibleIds(ids);
    } catch (err) {
      setEligibleIds(new Set());
    }
  };

  const isDeletable = (av) => eligibleIds != null && eligibleIds.has(av.avatar_id);
  const NOT_DELETABLE_NOTE = 'Available 60 days after the last paid subscription in this Avatar\u2019s Family Group ends. See Due for Deletion.';

  const filtered = avatars.filter(av => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = `${av.first_name} ${av.last_name}`.toLowerCase();
    return name.includes(q) || (av.owner_email || '').toLowerCase().includes(q);
  });

  const handleRowClick = (id) => {
    setExpandedRow(prev => (prev === id ? null : id));
    setMenuOpenId(null);
  };

  const handleDownload = async (av) => {
    setMenuOpenId(null);
    try {
      const token = localStorage.getItem('token');
      const base = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/+$/, '')
        : 'https://sayagainservices.sayagain.ai/api';
      const url = `${base}/admin/avatars/${av.avatar_id}/export`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safeName = `${av.first_name}_${av.last_name}`.replace(/\s+/g, '_') || `avatar_${av.avatar_id}`;
      link.download = `${safeName}_backup.txt`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      showError('Download failed: ' + (err.message || 'Unknown error'));
    }
  };

  const openDeleteModal = (av) => {
    setMenuOpenId(null);
    if (!isDeletable(av)) {
      showError(NOT_DELETABLE_NOTE);
      return;
    }
    setDeleteModal({ open: true, avatar: av, typedName: '', error: '' });
  };

  const closeDeleteModal = () => setDeleteModal({ open: false, avatar: null, typedName: '', error: '' });

  const handleDeleteConfirm = async () => {
    const { avatar, typedName } = deleteModal;
    const expectedName = `${avatar.first_name} ${avatar.last_name}`.trim();
    if (typedName.trim() !== expectedName) {
      setDeleteModal(prev => ({ ...prev, error: `Type "${expectedName}" exactly to confirm.` }));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await apiClient.delete(`/admin/users/${avatar.owner_id}/avatars/${avatar.avatar_id}`, token);
      setAvatars(prev => prev.filter(a => a.avatar_id !== avatar.avatar_id));
      fetchEligibility();
      closeDeleteModal();
      showSuccess('Avatar deleted successfully');
    } catch (err) {
      showError('Delete failed: ' + (err.message || 'Unknown error'));
    }
  };

  const formatDate = (s) => {
    if (!s) return 'N/A';
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const confidenceColor = (score) => {
    if (score >= 70) return '#16a34a';
    if (score >= 40) return '#d97706';
    return '#dc2626';
  };

  if (loading) {
    return <Layout><div className="users-loading">Loading avatars...</div></Layout>;
  }

  return (
    <Layout>
      <div className="users-page">
        {/* Header */}
        <div className="users-page-header">
          <div className="header-left">
            <h1 className="page-title">Avatars</h1>
          </div>
          <div className="header-right">
            <input
              type="text"
              placeholder="Search by name or owner email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', width: '280px', outline: 'none' }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Owner</th>
                <th>Members</th>
                <th>Confidence</th>
                <th>Documents</th>
                <th>Checklist</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-users">
                    <div className="empty-state">{search ? 'No avatars match your search.' : 'No avatars found.'}</div>
                  </td>
                </tr>
              ) : (
                filtered.map((av, idx) => {
                  const fullName = `${av.first_name} ${av.last_name}`.trim() || 'Unnamed';
                  const isExpanded = expandedRow === av.avatar_id;
                  const isMenuOpen = menuOpenId === av.avatar_id;

                  return (
                    <React.Fragment key={av.avatar_id}>
                      <tr
                        style={{ cursor: 'pointer', background: isExpanded ? '#f9fafb' : undefined }}
                        onClick={() => handleRowClick(av.avatar_id)}
                      >
                        <td className="row-number">{idx + 1}</td>
                        <td>
                          <div className="user-name-cell">
                            <div className="user-avatar-small">{(av.first_name || 'A').charAt(0).toUpperCase()}</div>
                            <div className="user-name-info">
                              <div className="user-name">{fullName}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '13px' }}>
                            <div style={{ fontWeight: 500 }}>{av.owner_name || '—'}</div>
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>{av.owner_email || ''}</div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>{av.member_count ?? 0}</td>
                        <td>
                          <span style={{ fontWeight: 600, color: confidenceColor(av.confidence_score ?? 0) }}>
                            {av.confidence_score != null ? `${av.confidence_score}%` : '—'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>{av.documents_uploaded ?? 0}</td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#374151' }}>{av.checklist_progress || '—'}</span>
                        </td>
                        <td className="date-cell">{formatDate(av.created_at)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ position: 'relative' }} ref={isMenuOpen ? menuRef : null}>
                            <button
                              className="action-btn"
                              title="Actions"
                              onClick={() => setMenuOpenId(isMenuOpen ? null : av.avatar_id)}
                              style={{ padding: '4px 8px', fontSize: '18px', lineHeight: 1, letterSpacing: '1px' }}
                            >
                              ···
                            </button>
                            {isMenuOpen && (
                              <div style={{
                                position: 'absolute', right: 0, top: '100%', zIndex: 100,
                                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '160px', overflow: 'hidden',
                              }}>
                                <button
                                  style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px' }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                  onClick={() => handleDownload(av)}
                                >
                                  Download Backup
                                </button>
                                {isDeletable(av) ? (
                                  <button
                                    style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#dc2626' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    onClick={() => openDeleteModal(av)}
                                  >
                                    Delete
                                  </button>
                                ) : (
                                  <button
                                    disabled
                                    title={NOT_DELETABLE_NOTE}
                                    style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'none', cursor: 'not-allowed', fontSize: '13px', color: '#9ca3af' }}
                                  >
                                    Delete (not yet available)
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr style={{ background: '#f9fafb' }}>
                          <td colSpan="9" style={{ padding: '12px 24px 16px 56px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', fontSize: '13px', color: '#374151' }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Relation to Owner</div>
                                <div style={{ marginTop: '4px' }}>{av.relation ? av.relation.replace(/_/g, ' ') : '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Checklist Progress</div>
                                <div style={{ marginTop: '4px' }}>{av.checklist_progress || '—'} questions answered</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Family Group Members</div>
                                <div style={{ marginTop: '4px' }}>{av.member_count ?? 0} connected</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Documents</div>
                                <div style={{ marginTop: '4px' }}>{av.documents_uploaded ?? 0} uploaded</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Delete confirmation modal */}
        {deleteModal.open && deleteModal.avatar && (
          <div className="confirm-modal-overlay" onClick={closeDeleteModal}>
            <div className="confirm-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
              <div className="confirm-modal-header">
                <h3>Delete Avatar</h3>
              </div>
              <div className="confirm-modal-body">
                <p>
                  This will permanently delete <strong>{`${deleteModal.avatar.first_name} ${deleteModal.avatar.last_name}`.trim()}</strong> and all associated data. This cannot be undone.
                </p>
                <p style={{ marginTop: '12px', marginBottom: '6px', fontSize: '13px' }}>
                  Type <strong>{`${deleteModal.avatar.first_name} ${deleteModal.avatar.last_name}`.trim()}</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteModal.typedName}
                  onChange={e => setDeleteModal(prev => ({ ...prev, typedName: e.target.value, error: '' }))}
                  placeholder="Avatar name"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  autoFocus
                />
                {deleteModal.error && (
                  <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{deleteModal.error}</p>
                )}
              </div>
              <div className="confirm-modal-actions">
                <button className="confirm-modal-btn confirm-modal-cancel" onClick={closeDeleteModal}>
                  Cancel
                </button>
                <button className="confirm-modal-btn confirm-modal-confirm confirm-modal-danger" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Avatars;
